import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import { completeOnboarding } from '@/api/client';
import { useUserStore } from '@/store/userStore';
import OrnateFrame from '@/components/OrnateFrame';
import DateField from '@/components/DateField';

/**
 * Three-step onboarding instead of one wall of inputs.
 * Each step has its own atmosphere — feels like a ritual, not a form.
 */
export default function Onboarding() {
  const { t, i18n } = useTranslation(); // i18n used in finish() for language detection
  const navigate = useNavigate();
  const { setUser, setNeedsOnboarding } = useUserStore();

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [birth, setBirth] = useState('');
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  // Birth date is valid when it looks complete in either format:
  // DD.MM.YYYY (10 chars) or YYYY-MM-DD (10 chars).
  const birthLooksValid = /^(\d{2}\.\d{2}\.\d{4}|\d{4}-\d{2}-\d{2})$/.test(birth);

  const canAdvance =
    (step === 0 && name.length > 0) ||
    (step === 1 && birthLooksValid) ||
    (step === 2 && question.length > 2);

  const finish = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Pick up any referral id we stashed when the user first opened
      // the app via someone's link.
      let referrer_id: number | null = null;
      try {
        const raw = localStorage.getItem('referrer_id');
        if (raw) referrer_id = Number(raw) || null;
      } catch {}

      const updated = await completeOnboarding({
        display_name: name,
        birth_date: birth,
        main_question: question,
        language: (i18n.language.startsWith('ru') ? 'ru' : 'en') as 'ru' | 'en',
        referrer_id,
      });
      // Clean up — we only need it for the initial signup.
      try { localStorage.removeItem('referrer_id'); } catch {}
      // Trigger the first-visit welcome tour on the next Home render.
      try { localStorage.setItem('tour_pending', '1'); } catch {}

      setUser(updated);
      setNeedsOnboarding(false);
      navigate('/', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="mx-auto max-w-md px-6 pb-16 pt-4">
      <div className="mb-6 flex items-center justify-center gap-2">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              i === step ? 'w-8 bg-gold-400' : 'w-3 bg-gold-500/30'
            }`}
          />
        ))}
      </div>

      <OrnateFrame className="rounded-3xl bg-night-800/40 p-6 backdrop-blur-sm">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.35 }}
            className="min-h-[280px]"
          >
            {step === 0 && (
              <Step
                title={t('onboarding.name_label')}
                hint={t('onboarding.subtitle')}
                icon="✦"
              >
                <input
                  autoFocus
                  className="mystic-input"
                  placeholder={t('onboarding.name_placeholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={64}
                />
              </Step>
            )}

            {step === 1 && (
              <Step
                title={t('onboarding.birth_label')}
                hint={t('onboarding.birth_hint')}
                icon="☾"
              >
                <DateField
                  value={birth}
                  onChange={setBirth}
                  autoFocus
                />
              </Step>
            )}

            {step === 2 && (
              <Step
                title={t('onboarding.question_label')}
                hint={t('onboarding.question_hint')}
                icon="❋"
              >
                <textarea
                  autoFocus
                  className="mystic-input min-h-[100px] resize-none"
                  placeholder={t('onboarding.question_placeholder')}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  maxLength={280}
                />
                <div className="mt-1 flex items-start justify-between gap-3">
                  <p className="text-[11px] italic leading-snug text-gold-300/55">
                    {t('onboarding.question_change_note')}
                  </p>
                  <span className="shrink-0 text-xs text-gold-300/40">
                    {question.length}/280
                  </span>
                </div>
              </Step>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-6 flex justify-between gap-3">
          <button
            type="button"
            className="btn-ghost text-sm disabled:opacity-30"
            onClick={back}
            disabled={step === 0}
          >
            ←
          </button>
          {step < 2 ? (
            <button
              type="button"
              className="btn-primary flex-1 disabled:opacity-40"
              disabled={!canAdvance}
              onClick={next}
            >
              {t('onboarding.next')}
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary flex-1 disabled:opacity-40"
              disabled={!canAdvance || submitting}
              onClick={finish}
            >
              {t('onboarding.continue')}
            </button>
          )}
        </div>
      </OrnateFrame>

      {/* Local style for inputs — keeps them all consistent. */}
      <style>{`
        .mystic-input {
          width: 100%;
          background: rgba(11, 8, 32, 0.6);
          border: 1px solid rgba(212, 168, 46, 0.3);
          color: #fff6c7;
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 1.15rem;
          padding: 0.85rem 1rem;
          border-radius: 0.75rem;
          outline: none;
          transition: border-color 0.2s;
        }
        .mystic-input:focus { border-color: #e8c356; }
        .mystic-input::placeholder { color: rgba(245, 217, 122, 0.4); }
      `}</style>
    </main>
  );
}


function Step({
  icon,
  title,
  hint,
  children,
}: {
  icon: string;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="mb-3 text-center text-4xl text-gold-400 float-soft">
        {icon}
      </div>
      <h2 className="mb-1 text-center font-serif text-xl text-gold-200">{title}</h2>
      <p className="mb-5 text-center text-sm italic text-gold-300/70">{hint}</p>
      {children}
    </>
  );
}
